import { Box, CircularProgress, debounce, Skeleton, Stack, styled } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';

import { useGetMedicalConditions } from '@/components/Patient/hooks/useGetMedicalConditions';
import { useUpdatePatientDetails } from '@/components/Patient/hooks/useUpdatePatientDetails';
import { FF_PATIENT_DETAILS_SKELETON_ENABLED } from '@/constants/featureFlags';
import { getAuthData } from '@/data/service/authService';
import { useFeatureFlags } from '@/hooks';
import { useCreateDassAssessment, useDeactivateDass } from '@/hooks/dass/useDass';
import { useAppStore } from '@/state-management';
import { generateUUid } from '@/utils/generateUUID';
import type { DirtyFields } from '@/utils/reactHookForm';
import { dirtyValues } from '@/utils/reactHookForm';
import { toast } from '@montugroup/design-system';
import type { GetPatientDetailsInclude } from '@montugroup/pms-api-contracts';
import { logger } from 'nx/src/utils/logger';
import { FormContainer, MultiSelectElement, SelectElement, TextFieldElement } from 'react-hook-form-mui';
import FormSection from '../../FormSection';
import { PATIENT_DETAILS_FORM_NAMES } from '../../Patient.constants';
import DassAssessmentScoreStatus from './DassAssessmentScoreStatus/DassAssessmentScoreStatus';
import type { MedicalConditionDefaultValues, MedicalConditionProps } from './MedicalCondition.types';

const StyledMultiSelectElement = styled(MultiSelectElement)(() => ({
  width: '100%',
  '.MuiChip-root': {
    margin: '0 3px',
  },
  ...{
    '.MuiChip-deleteIcon': {
      display: 'none',
    },
  },
}));

const StyledFieldContainer = styled(Box)({
  marginTop: 15,
  marginBottom: 15,
});

export const MedicalCondition = (props: MedicalConditionProps) => {
  const {
    editable,
    defaultValues,
    dassAssessmentDetails,
    dassAssessmentScoreDetail,
    isDassScoreDetailLoading,
    userEmail,
    fetchDassAssessment,
    onSubmit,
    isLoading,
  } = props;

  const FORM_NAME: GetPatientDetailsInclude = PATIENT_DETAILS_FORM_NAMES.PATIENT_MEDICAL_INFO;

  const { isDoc } = getAuthData();
  const { flags } = useFeatureFlags();

  const {
    data: medicalConditionList,
    isLoading: isLoadingMedicalConditions,
    isError: isErrorMedicalConditions = [],
  } = useGetMedicalConditions();
  const { firstName: patientFirstName } = useAppStore((state) => state.getPatientBasicInfo());
  const { email } = useAppStore((state) => state.getPatientContactDetails());
  const getPatientId = useAppStore.use.getPatientId();
  const patientId = getPatientId();

  const { isPending, isError, isSuccess, mutateAsync } = useUpdatePatientDetails(FORM_NAME);

  const isPrimaryConditionOtherChosen = useRef(false);
  const isSecondaryConditionOtherChosen = useRef(false);

  if (isErrorMedicalConditions) {
    toast.error('An error has occurred loading the list of medical conditions');
  }

  const isSkeletonEnabled = flags[FF_PATIENT_DETAILS_SKELETON_ENABLED];

  /**
   * Medical conditions are set as strings in the database and on the state on the page.
   * Specifically, medical_condition_1 is set as a string of array, e.g. '[1,2,3]'
   *
   * We convert them here to numbers so they can work with the forms, then, before saving,
   * we convert them back to strings.
   *
   * Also, we have to retain how some of the fields are named since they have to work both in V2 and V3.
   */
  let parsed_medical_condition_1;
  try {
    parsed_medical_condition_1 = defaultValues?.medicalCondition1 ? JSON.parse(defaultValues?.medicalCondition1) : [];
  } catch (_error) {
    logger.warn('A problem occurred while parsing medical_condition_1. Setting to default value.');
    parsed_medical_condition_1 = [];
  }

  const parsedMedicalConditions = {
    medicalCondition1: parsed_medical_condition_1,
    medicalCondition2: defaultValues?.medicalCondition2 && Number(defaultValues?.medicalCondition2),
    medicalCondition3: defaultValues?.medicalCondition3 && Number(defaultValues?.medicalCondition3),
    medicalCondition4: defaultValues?.medicalCondition4 && Number(defaultValues?.medicalCondition4),
    othersComment1: defaultValues?.othersComment1,
    othersComment2: defaultValues?.othersComment2,
  };

  const formContext = useForm<FieldValues>({
    defaultValues: parsedMedicalConditions,
    values: parsedMedicalConditions,
  });

  const { handleSubmit, reset } = formContext;

  const isEffectivelyEditable = editable && typeof onSubmit === 'function';
  const watchedPrimaryCondition = useWatch({ name: 'medicalCondition1', control: formContext.control });
  const watchedSecondaryConditions = useWatch({
    name: ['medicalCondition2', 'medicalCondition3', 'medicalCondition4'],
    control: formContext.control,
  }).flat();

  const { deactivateDass } = useDeactivateDass();
  const { createDassAssessment } = useCreateDassAssessment();

  const triggerDeactivateDass = useCallback(async () => {
    await deactivateDass({
      patientId: String(patientId as number),
      userEmail: userEmail ?? '',
    });
  }, [deactivateDass, patientId, userEmail]);

  const triggerCreateDassAssessment = useCallback(async () => {
    await createDassAssessment({
      patientId: String(patientId as number),
      userEmail: userEmail ?? '',
      uuid: dassAssessmentDetails?.uuid || generateUUid(),
      daysLinkValid: 5,
      patientEmail: email ?? '',
      patientName: patientFirstName ?? '',
    });
  }, [createDassAssessment, dassAssessmentDetails?.uuid, email, patientFirstName, patientId, userEmail]);

  /*
    Critical medical conditions like PTSD (id 1), Depression (id 2) or Anxiety (id 3) trigger the Psychological Diagnostic Assessment (PDA)
    which is related to DASS. If any of these are selected in 'medicalCondition1' then we send the DASS assessment email to the patient and
    we display the 'DASS Assessment Scores' section at the top of the Medical Condition form.
  */
  const medicalConditionsRequiringDass = [1, 2, 3];
  const hasPrimaryDassMatch = watchedPrimaryCondition?.some((medicalCondition: number) =>
    medicalConditionsRequiringDass.includes(medicalCondition),
  );
  const hasSecondaryDassMatch = watchedSecondaryConditions.some((medicalCondition) =>
    medicalConditionsRequiringDass.includes(medicalCondition),
  );

  const patientHasDassMedicalConditions = hasPrimaryDassMatch || hasSecondaryDassMatch;
  const hasAlreadySentDassAssessment = Boolean(dassAssessmentDetails?.link_send_count);
  const shouldSendDassAssessment = editable && !hasAlreadySentDassAssessment;
  const showDassSection =
    (patientHasDassMedicalConditions && hasAlreadySentDassAssessment) || dassAssessmentScoreDetail?.anxiety?.level;
  const hasDassScore = Boolean(dassAssessmentScoreDetail?.anxiety?.level);

  useEffect(() => {
    if (!formContext.formState.isDirty) {
      return;
    }

    const shouldTriggerCreateDass =
      patientHasDassMedicalConditions && !isDassScoreDetailLoading && !hasDassScore && shouldSendDassAssessment;
    const shouldTriggerDeactivateDass = !patientHasDassMedicalConditions && hasAlreadySentDassAssessment;

    /*
     Send a Dass Assessment email to the patient if:
     - The Patient has Dass-related medical conditions
     - The request to get the Dass score is not loading
     - A Dass email was not already sent to the patient
     - The Patient does not already have a Dass assessment score
     - The user has permissions to edit the medical conditions
    */
    if (shouldTriggerCreateDass) {
      triggerCreateDassAssessment();
    }

    /*
     Deactivate the patient from needed a Dass Assessment if:
     - The Patient doesn't have Dass-related medical conditions
     - The Patient has previously been sent a Dass assessment
    */
    if (shouldTriggerDeactivateDass) {
      triggerDeactivateDass();
    }
  }, [
    formContext.formState.isDirty,
    patientHasDassMedicalConditions,
    hasAlreadySentDassAssessment,
    triggerCreateDassAssessment,
    triggerDeactivateDass,
    isDassScoreDetailLoading,
    hasDassScore,
    shouldSendDassAssessment,
  ]);

  const medicalConditionOtherId = medicalConditionList?.find(
    (item) => item.condition_name.toLowerCase() === 'other',
  )?.id;

  isPrimaryConditionOtherChosen.current = watchedPrimaryCondition?.includes(medicalConditionOtherId);
  isSecondaryConditionOtherChosen.current = watchedSecondaryConditions.includes(medicalConditionOtherId);

  // Get all selected conditions in form
  const allSelectedConditions = useWatch({
    name: ['medicalCondition1', 'medicalCondition2', 'medicalCondition3', 'medicalCondition4'],
    control: formContext.control,
  }).flat();

  const conditionsAvailableForSingleSelect = medicalConditionList?.map((item) => {
    return {
      id: item.id,
      label: item.condition_name,
      disabled: item.id !== medicalConditionOtherId && allSelectedConditions.includes(item.id),
    };
  });

  // adds a `None` option to clear the value of the select
  conditionsAvailableForSingleSelect?.push({ id: '', label: 'None', disabled: false });

  const conditionsAvailableForMultiSelect = medicalConditionList
    ?.filter((item) => !watchedSecondaryConditions.includes(item.id) || item.id === medicalConditionOtherId)
    .map((item) => ({
      id: item.id,
      label: item.condition_name,
    }));

  // This converts all the form values into strings. The transform is needed because the DB stores the medical
  // conditions as strings, including 'medicalCondition1' which is stored as a string of "[1,2,3,4]".
  const transformFormValues = (attributes: MedicalConditionDefaultValues | Partial<unknown>) => {
    const transformed: Record<string, string | null> = {};

    Object.entries(attributes).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        transformed[key] = JSON.stringify(value);
      } else if (typeof value === 'number') {
        transformed[key] = `${value}`;
      } else {
        transformed[key] = value;
      }
    });

    return transformed;
  };

  const handleOnSubmit = useCallback(
    (formData: FieldValues | undefined) => {
      const {
        formState: { isDirty, dirtyFields },
      } = formContext;

      if (!isDirty || !dirtyFields || Object.keys(dirtyFields).length === 0) {
        return;
      }

      const modifiedDirtyFields = { ...dirtyFields };
      const modifiedFormData = { ...formData };

      if (!isPrimaryConditionOtherChosen.current) {
        modifiedDirtyFields.othersComment1 = true;
        modifiedFormData.othersComment1 = '';
      }

      if (!isSecondaryConditionOtherChosen.current) {
        modifiedDirtyFields.othersComment2 = true;
        modifiedFormData.othersComment2 = '';
      }

      const updatedDirtyFields = dirtyValues(modifiedDirtyFields as DirtyFields<unknown>, modifiedFormData);
      const autosavePayload = transformFormValues(updatedDirtyFields);

      if (getPatientId()) {
        mutateAsync(Number(getPatientId()), autosavePayload);
      }

      // Legacy global save
      if (onSubmit) {
        const globalSavePayload = {
          medical_condition_1: formData?.medicalCondition1 ? JSON.stringify(formData?.medicalCondition1) : '[]',
          medical_condition_2: formData?.medicalCondition2 ? `${formData?.medicalCondition2}` : '',
          medical_condition_3: formData?.medicalCondition3 ? `${formData?.medicalCondition3}` : '',
          medical_condition_4: formData?.medicalCondition4 ? `${formData?.medicalCondition4}` : '',
          others_1_comment: isPrimaryConditionOtherChosen.current ? formData?.othersComment1 : '',
          others_2_comment: isSecondaryConditionOtherChosen.current ? formData?.othersComment2 : '',
        };
        onSubmit(globalSavePayload);
      }
    },
    [formContext, getPatientId, mutateAsync, onSubmit],
  );

  /*
    The below code fires off the 'handleOnSubmit' directly (instead of using the submit in the <FormSection>)
    when the multi-select field is changed. The reason for doing this is because the non-native select field
    (<MultiSelect>) was being a bit buggy with the 'isDirty' RHF state and sometimes not firing off the submit
    due to RHF state-update race conditions. Doing the following allows us to 'watch' the field and fire the
    submit directly without relying on the 'onBlur' in the <FormSection>.
  */

  const multiSelectField = useWatch({
    name: ['medicalCondition1'],
    control: formContext.control,
  })
    .flat()
    .toString();

  const debouncedSubmit = useRef(
    debounce(() => {
      handleSubmit(handleOnSubmit)();
    }, 1000),
  ).current;

  useEffect(() => {
    debouncedSubmit();
  }, [debouncedSubmit, multiSelectField]);

  /**
   * Need to reset the form to clear the form isDirty flag and dirtyField
   * otherwise, it will continue to include those fields in the payload
   */
  useEffect(() => {
    if (!isPending && isSuccess) {
      reset(formContext.getValues());
    }
  }, [formContext, isPending, isSuccess, reset]);

  return (
    <FormSection
      title="Medical Condition"
      formName={undefined}
      formContext={formContext}
      onSubmit={undefined}
      noBorder
      transform={transformFormValues}
      formSectionStatus={{ isPending, isError, isSuccess }}
    >
      <FormContainer formContext={formContext}>
        {showDassSection && (
          <Box paddingTop={3}>
            {(isLoading || isLoadingMedicalConditions) && isSkeletonEnabled ? (
              <Box width="100%">
                <Skeleton variant="rounded" height={16} width={160} />
                <Box mt={0.25}>
                  <Skeleton variant="rounded" height={42} width="100%" />
                </Box>
              </Box>
            ) : (
              <DassAssessmentScoreStatus
                dassAssessmentDetails={dassAssessmentDetails}
                dassAssessmentScoreDetail={dassAssessmentScoreDetail}
                fetchDassAssessment={fetchDassAssessment}
                userEmail={userEmail}
                isDoc={isDoc}
                disabled={!isEffectivelyEditable}
                patientInfo={{
                  patientId,
                  patientEmail: email,
                  patientFirstName: patientFirstName,
                }}
              />
            )}
          </Box>
        )}
        {isLoading ? (
          isSkeletonEnabled ? (
            <Box>
              {Array.from({ length: 4 }).map((_, index) => (
                <StyledFieldContainer key={index}>
                  <Skeleton variant="rounded" height={55} />
                </StyledFieldContainer>
              ))}
            </Box>
          ) : (
            <Stack height="30vh" justifyContent="center" alignItems="center">
              <CircularProgress size={25} />
            </Stack>
          )
        ) : (
          <Box data-testid={'medical-condition-form'}>
            {/* Primary Condition */}
            <StyledFieldContainer>
              <StyledMultiSelectElement
                label={'Primary Condition'}
                fullWidth
                name="medicalCondition1"
                options={conditionsAvailableForMultiSelect ?? []}
                showChips
                data-testid={'primary-condition'}
                disabled={!isEffectivelyEditable}
              />
            </StyledFieldContainer>
            {isPrimaryConditionOtherChosen.current && (
              <StyledFieldContainer onBlur={handleSubmit(handleOnSubmit)}>
                <TextFieldElement
                  label={'Comment'}
                  name="othersComment1"
                  variant="outlined"
                  data-testid="primary-condition-comment"
                  fullWidth
                  multiline
                  minRows={3}
                  inputProps={{ maxLength: 500 }}
                  disabled={!isEffectivelyEditable}
                />
              </StyledFieldContainer>
            )}
            {/* Secondary Conditions */}
            <StyledFieldContainer>
              <SelectElement
                label={'Secondary Condition 1'}
                name={'medicalCondition2'}
                options={conditionsAvailableForSingleSelect}
                fullWidth
                data-testid={'secondary-condition-1'}
                disabled={!isEffectivelyEditable}
                onChange={handleSubmit(handleOnSubmit)}
              />
            </StyledFieldContainer>
            <StyledFieldContainer>
              <SelectElement
                label={'Secondary Condition 2'}
                name={'medicalCondition3'}
                options={conditionsAvailableForSingleSelect}
                fullWidth
                data-testid={'secondary-condition-2'}
                disabled={!isEffectivelyEditable}
                onChange={handleSubmit(handleOnSubmit)}
              />
            </StyledFieldContainer>
            <StyledFieldContainer>
              <SelectElement
                label={'Secondary Condition 3'}
                name={'medicalCondition4'}
                options={conditionsAvailableForSingleSelect}
                fullWidth
                data-testid={'secondary-condition-3'}
                disabled={!isEffectivelyEditable}
                onChange={handleSubmit(handleOnSubmit)}
              />
            </StyledFieldContainer>
            {isSecondaryConditionOtherChosen.current && (
              <StyledFieldContainer onBlur={handleSubmit(handleOnSubmit)}>
                <TextFieldElement
                  label={'Comment'}
                  name="othersComment2"
                  variant="outlined"
                  data-testid="secondary-condition-comment"
                  fullWidth
                  multiline
                  minRows={3}
                  inputProps={{ maxLength: 500 }}
                  disabled={!isEffectivelyEditable}
                />
              </StyledFieldContainer>
            )}
          </Box>
        )}
      </FormContainer>
    </FormSection>
  );
};
